import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import * as Rx from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { CacheService } from '../utility/cache.service';
import { AppSettingService } from '../app.service';
import { UserInfoService} from './user-info.service';
import { MapAPIService } from '../map/map-api.service';

export interface IAnonymousUser {
	Email: string,
	EmailStatus?: boolean,
	InstitutionCode: string
};

export class NewAnonymousUser implements IAnonymousUser {
	constructor() { }
	Email = "";
	EmailStatus = null as any;
	InstitutionCode = "";
}

export interface ISearchUserFilter {
	Limit: number;
	Offset: number;
	ZoneId?: string;
	SchoolId?: number;
	PersonTypeId?: number;
	StudentGradeId?: number;
	Active?: boolean;
	PersonId?: number;
	PersonCode?: string;
	FirstName?: string;
	LastName?: string;
	Username?: string;
}

export interface ISearchUser {
	PersonId: string;
	PersonCode?: string;
	LastName: string;
	FirstName: string;
	Active?: boolean;
	Username?: string;
	Approved?: boolean;
	ZoneId?: string;
	ZoneClient?: string;
	PersonRoles?: IPersonRole[],
	isSelected?: boolean; // used in UI
}

export interface ISearchZoneUser {
	PersonId: string;
	LastName: string;
	FirstName: string;
	BirthDate: string,
	Role: string,
	StudentGrade: string,
	Email: string,
	SchoolName: string
}

export interface ISearchUserResult {
	Client: string;
	SchoolName: string;
	PersonType: string;
	Total: number;
	Users: ISearchUser[];
}

export interface IPersonRole {
	Role: string,
	RoleId: string,
	RoleName: string,
	StudentGrade: string,
	GradeName: string
}

export interface IPerson {
	ZoneId: string,
	DistrictName: string,
	ActiveAccount: boolean,
	ApprovedAccess?: boolean,
	StudentGrade: string,
	PersonId: string,
	PersonGuid: string,
	PersonCode: string,
	LastName: string,
	FirstName: string,
	MI: string,
	ManualUpdate: boolean,
	FullName: string,
	BirthDate: string,
	Gender: string,
	UserName: string,
	Password: string,
	LastPasswordChangeDate: string,
	HighestRole: string,
	PersonRoles: IPersonRole [],
	Email: string,
	ZipCode: string,
	ZoneClient: string
}

export interface IContact {
	Email: string,
	Address1: string,
	Address2: string,
	City: string,
	State: string,
	ZipCode: string,
	PhoneArea: string,
	PhoneNumber: string,
	PhoneExt: string,
	WkPhoneArea: string,
	WkPhoneNumber: string,
	WkPhoneExt: string,
	CellPhoneArea: string,
	CellPhoneNumber: string,
	CellPhoneExt: string,
	ContacterName: string,
	ContacterRelation: string,
	ContacterEmail: string,
	ContacterPhoneArea: string,
	ContacterPhoneNumber: string,
	ContacterPhoneExt: string,
	ContacterWkPhoneArea: string,
	ContacterWkPhoneNumber: string,
	ContacterWkPhoneExt: string,
	ContacterCellPhoneArea: string,
	ContacterCellPhoneNumber: string,
	ContacterCellPhoneExt: string,
	Comment: string
}

export interface IPersonSchool {
	SchoolId: string,
	SchoolGuid: string,
	SchoolCode: string,
	SchoolName: string,
	City: string,
	Type: string,
	PersonRole: IPersonRole
}

export interface IClassTeacher {
	PersonId: string,
	PersonGuid: string,
	PersonCode: string,
	LastName: string,
	FirstName: string
}

export interface IPersonClass {
	ClassId: string,
	ClassGuid: string,
	Course: string,
	Section: string,
	Subject: string,
	Period: string,
	ClassName: string,
	ActiveClass: boolean,
	Teacher: IClassTeacher,
	School: IPersonSchool
}

export interface IPersonInfo {
	Person: IPerson,
	Schools: IPersonSchool[],
	Classes: IPersonClass[],
	Contact: IContact
}

export interface IRole {
	RoleId: string,
	Role: string,
	RoleName: string,
	checked: boolean  //used in UI
}

export interface IStudentGrade {
	GradeId: string,
	Grade: string,
	GradeName: string
}

export interface ISaveSchoolRole {
	SchoolId: string,
	RoleId: string
}

export interface ISaveSystemUser extends IContact {
	ZoneId: string,
	ZoneClient: string,
	PersonId: string,
	PersonGuid: string,
	LastName: string,
	FirstName: string,
	MI: string,
	BirthDate: string,
	Gender: "Male" |"Female"|"",
	PersonCode: string,
	ActiveAccount: boolean
	ManualUpdate: boolean,
	PersonTypeIds: string[],
	StudentGradeId: string,
	UserName: string,
	Password: string,
	ApprovedAccess: boolean,
	SchoolRoles: ISaveSchoolRole[]
}

export interface IUserAccess {
	PersonId: string,
	ActiveAccount: boolean,
	ApprovedAccess? : boolean,
	Email: string,
	UserName: string,
	Password: string,
	ConfirmPassword: string,
	ChangeUserPW: boolean,
	EmailChange: boolean,
	SingleUse: boolean,
	lastPasswordChangeDate?: string, // used in UI
	last3Sessions?: string[] // used in UI
}

@Injectable({
	providedIn: 'root'
})
export class UserAPIService {
	constructor(
		private cacheSrv: CacheService,
		private http: HttpClient,
		private userInfo: UserInfoService
	) { }

	private url = AppSettingService.appConfig.JourneyAPIUrl + "User";

	//#region zone admin
	searchZonePersons(role: string, schoolId: number, kw: string, limit: number, offset: number): Rx.Observable<ISearchZoneUser[]> {
		kw = kw.trim();
		return this.http.get<any>(
			this.url + '/Zone?role=' + role + '&schoolId=' + (schoolId == null ? '' : encodeURIComponent(schoolId)) + '&kw=' + encodeURIComponent(kw) + '&limit=' + limit.toString() + '&offset=' + offset.toString(),
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	getZonePerson(personId: number): Rx.Observable<IPersonInfo> {
		return this.http.get<IPersonInfo>(
			this.url + "/Zone/" + personId.toString(),
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}
	//#endregion zone admin

	register(reg:any): Rx.Observable<any> {
		return this.http.post<any>(
			this.url + "/Signup",
			reg
		);
	}

	globalRegister(reg: any): Rx.Observable<any> {
		return this.http.post<any>(
			this.url + "/GlobalSignup",
			reg
		);
	}

	updateGlobalAccountReqiretCount(): Rx.Observable<any> {
		return this.http.put<any>(
			this.url + "/GlobalSignup/Require",
			null,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		)
	}

	getGlobalAccountReqiretCount(): Rx.Observable<any> {
		return this.http.get<any>(
			this.url + "/GlobalSignup/Require",
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		)
	}

	sendEmailVerification(email: string): Rx.Observable<any> {
		return this.http.get<any>(
			this.url + "/VerifyEmail/Send?email=" + encodeURIComponent(email),
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	confirmEmailVerification(email: string, code:string): Rx.Observable<any> {
		return this.http.get<any>(
			this.url + "/VerifyEmail/Confirm?email=" + encodeURIComponent(email) + "&code=" + encodeURIComponent(code),
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	checkUserNameExist(username: string, zoneName: string): Rx.Observable<any> {
		return this.http.get<any>(
			this.url + "/CheckUserNameExist?username=" + encodeURIComponent(username) + "&zone=" + encodeURIComponent(zoneName)
		);
	}

	checkValidationCodeExist(code:string): Rx.Observable<any> {
		return this.http.get<any>(
			this.url  + '/CheckValidationCodeExist?code=' + encodeURIComponent(code)
		);
	}

	resetPassword(req:any): Rx.Observable<any> {
		return this.http.post<any>(
			this.url + "/ResetPassword",
			req
		);
	}

	// Anonymous
	saveAnonymousUser(data: IAnonymousUser): Rx.Observable<any> {
		return this.http.post<any>(
			this.url + "/AnonymousUser",
			data,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	};

	getAnonymousUser(id: string): Rx.Observable<any> {
		return this.http.get<any>(
			this.url +  "/AnonymousUser/" + encodeURIComponent(id),
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	transferAnonymousUserData (anonymousUserLink: string): Rx.Observable<any> {
		return this.http.get<any>(
			this.url + '/AnonymousUser/' + encodeURIComponent(anonymousUserLink) + '/TransferData',
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	copyAnonymousSessionData(sessionId: string): Rx.Observable<any> {
		return this.http.get<any>(
			this.url + '/AnonymousUser/' + encodeURIComponent(sessionId) + '/CopySessionData',
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	getUserProfile(userId: string): Rx.Observable<any> {
		return this.http.get<any>(
			this.url + "/UserProfile" + (userId != null && userId.length > 0 ? "?UserId=" + userId : ""),
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	//#region system admin
	getSystemSearchResult(filter: ISearchUserFilter): Rx.Observable<ISearchUserResult> {
		return this.http.post<ISearchUserResult>(
			this.url + "/System/Search/Result",
			filter,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	getSystemPerson(personId: number): Rx.Observable<IPersonInfo> {
		return this.http.get<IPersonInfo>(
			this.url + "/System/" + personId.toString(),
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	saveSystemPerson(account: ISaveSystemUser): Rx.Observable<IPersonInfo> {
		return this.http.post<IPersonInfo>(
			this.url + "/System",
			account,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	updateSystemAccess(access: IUserAccess): Rx.Observable<IPersonInfo> {
		return this.http.post<IPersonInfo>(
			this.url + "/System/Access",
			access,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}
	//#endregion system admin

	//get all role except anonymous
	getRoles(): Rx.Observable<IRole[]> {
		return this.getAllRoles().pipe(map(
			response => {
				return response.filter(x => x.RoleId != "255");
			}
		));
	}

	getAllRoles(): Rx.Observable<IRole[]> {
		let l: string = this.url + "/Role";
		return this.cacheSrv.get(true, l);
	}

	getStudentGrades(): Rx.Observable<IStudentGrade[]> {
		let l: string = this.url + "/StudentGrade";
		return this.cacheSrv.get(true, l);
	}
}


import { IClassStudentPicker } from '../class/enrollment-api.service';
@Injectable({
	providedIn: 'root'
})
export class ActivityAPIService {
	constructor(
		private userInfo: UserInfoService,
		private http: HttpClient,
		private mapAPI: MapAPIService
	) { }

	private url = AppSettingService.appConfig.JourneyAPIUrl + "Activity";

	userInstitutionsVisited(userId: string, offset: number, limit: number): Rx.Observable<any> {
		return this.http.get<any>(
			this.url + '/Institution?userId=' + encodeURIComponent(userId) + '&offset= ' + offset.toString() + '&limit=' + limit.toString(),
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	getActivityTotal(userId: string, activityType: number, objectType: number, depth: number): Rx.Observable<any> {
		var link = "";
		if (depth == null) {
			link = this.url + '/TotalCount?userId=' + encodeURIComponent(userId) + '&activityType= ' + encodeURIComponent(activityType) + '&objectType=' + encodeURIComponent(objectType);
		}
		else {
			link = this.url + '/TotalCount?userId=' + encodeURIComponent(userId) + '&activityType= ' + encodeURIComponent(activityType) + '&objectType=' + encodeURIComponent(objectType) + '&depth=' + encodeURIComponent(depth);
		}
		return this.http.get<any>(
			link,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	getSummary(filter: IClassStudentPicker): Rx.Observable<any> {
		return this.http.post<any>(
			this.url + "/Summary",
			filter,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	getStats(filter: IClassStudentPicker): Rx.Observable<any> {
		return this.http.post<any>(
			this.url + "/Stats",
			filter,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	userOccupationMetrics(userId:string): Rx.Observable<any> {
		return this.mapAPI.getUserMap().pipe(switchMap(
			(response) => {
				return this.http.get<any>(
					this.url + '/Career/Metrics?userGuid=' + encodeURIComponent(userId) + '&careerVersion=' + encodeURIComponent(response.CareerVersion.Version),
					{
						withCredentials: true,
						headers: { 'Authorization': this.userInfo.authHeader(false) }
					}
				);
			}
		));
	}

	userOccupationVisited(userId: string, offset:number, limit:number): Rx.Observable<any> {
		return this.mapAPI.getUserMap().pipe(switchMap(
			response => {
				return this.http.get<any>(
					this.url + '/Career/Visited?userGuid=' + encodeURIComponent(userId) + '&careerVersion=' + encodeURIComponent(response.CareerVersion.Version) + '&offset=' + offset + '&limit=' + limit,
					{
						withCredentials: true,
						headers: { 'Authorization': this.userInfo.authHeader(false) }
					}
				);
			}
		));
	}

	log(objectType: string, activitytype: string, objectId: string, note: string, associatedObjectId: string): Rx.Observable<any> {
		var data = {
			"ActivityType": activitytype,
			"AssociatedObjectId": associatedObjectId,
			"Note": note,
			"ObjectId": objectId,
			"ObjectType": objectType,
			"InstitutionCode": this.userInfo.getInstitutionCode()
		};
		return this.http.post<any>(
			this.url + '/Log',
			data,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	getUserActivityStats(userGuid: string): Rx.Observable<any> {
		return this.http.get<any>(
			this.url + '/UserActivityStats/' + encodeURIComponent(userGuid),
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}
}


@Injectable({
	providedIn: 'root'
})
export class SessionAPIService {
	constructor(
		private userInfo: UserInfoService,
		private http: HttpClient
	) { }

	private url = AppSettingService.appConfig.JourneyAPIUrl + "Session";

	getSessionId(): Rx.Observable<string> {
		return this.http.get<any>(
			AppSettingService.appConfig.JourneyAPIUrl + "Session/SessionId"
		).pipe(map(
			response => {
				return response.Detail;
			}
		));
	}

	signOn(username: string, password: string, zone: string): Rx.Observable<any> {
		zone = (zone ?? "").trim();
		this.userInfo.clearStorage();
		return this.http.get<any>(
			this.url + "/SignOn?UserName=" + encodeURIComponent(username) + "&Password=" + encodeURIComponent(password) + "&zone=" + encodeURIComponent(zone)
		);
	}

	signOnAsOther(otherPersonId: string): Rx.Observable<any> {
		return this.http.get<any>(
			this.url + "/SignOnAsOther/" + encodeURIComponent(otherPersonId),
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	globalLogin(username: string, password: string): Rx.Observable<any> {
		this.userInfo.clearStorage();
		return this.http.get<any>(
			this.url + "/GlobalSignOn?UserName=" + encodeURIComponent(username) + "&Password=" + encodeURIComponent(password),
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	// get the cookie for anonymous user. We need to delay to let browser has chance to set the cookie
	anonymous(): Rx.Observable<any> {
		return this.http.get<any>(
			this.url + '/Anonymous'
		);
	}

	getSessionInfo(): Rx.Observable<any> {
		return this.http.get<any>(
			this.url,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	loginReport = {
		lastLogin: (userId: string): Rx.Observable<any> => {
			return this.http.get<any>(
				this.url + "/LoginReport/LastLogin?userGuid=" + userId,
				{
					withCredentials: true,
					headers: { 'Authorization': this.userInfo.authHeader(false) }
				}
			);
		},
		month: (userId: string, month: number, year: number): Rx.Observable<any> => {
			return this.http.get<any>(
				this.url + "/LoginReport/Month?userGuid=" + userId + "&month=" + month.toString() + "&year=" + year.toString(),
				{
					withCredentials: true,
					headers: { 'Authorization': this.userInfo.authHeader(false) }
				}
			);
		},
		day: (userId: string, month: number, year: number): Rx.Observable<any> => {
			return this.http.get<any>(
				this.url + "/LoginReport/Day?userGuid=" + userId + "&month=" + month.toString() + "&year=" + year.toString(),
				{
					withCredentials: true,
					headers: { 'Authorization': this.userInfo.authHeader(false) }
				}
			);
		},
		hour: (userId: string, month: number, day: number, year: number): Rx.Observable<any> => {
			return this.http.get<any>(
				this.url + "/LoginReport/Hour?userGuid=" + userId + "&month=" + month.toString() + "&year=" + year.toString() + "&dayNum=" + day,
				{
					withCredentials: true,
					headers: { 'Authorization': this.userInfo.authHeader(false) }
				}
			);
		},
		last3Sessions: (personId: number): Rx.Observable<any> => {
			return this.http.get<any>(
				this.url + "/LoginReport/Last3/" + encodeURIComponent(personId),
				{
					withCredentials: true,
					headers: { 'Authorization': this.userInfo.authHeader(false) }
				}
			);
		}
	}
}


@Injectable({
	providedIn: 'root'
})
export class WebAPIService {
	constructor(
		private http: HttpClient,
		private userInfo: UserInfoService
	) { }

	private url = AppSettingService.appConfig.WebAPIUrl;

	signOn(): Rx.Observable<any> {
		this.userInfo.clearStorage();
		return this.http.get<any>(
			this.url + "Session/SignOn",
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	signOff(): Rx.Observable<any> {
		return this.http.get<any>(
			this.url + "Session/SignOff"
		);
	}
}

